import { countryCodes } from "./countryCodes"
import { CONTINENT_LABELS, CONTINENT_ORDER, countryContinentByCode } from "./countryContinents"
import type { CountryContinent, CountryItem, CountryLocale } from "./types"

export function removeDiacritics(text: string) {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

export function filterExcludedCountries(
  countries: CountryItem[],
  excludedCountryCodes?: string[]
): CountryItem[] {
  if (!excludedCountryCodes?.length) {
    return countries
  }

  const excluded = new Set(excludedCountryCodes.map((code) => code.toUpperCase()))
  return countries.filter((country) => !excluded.has(country.code))
}

export function getCountryName(country: CountryItem, lang: CountryLocale = "en") {
  return country.name[lang] ?? country.name.en
}

export function sortCountriesByName(countries: CountryItem[], lang: CountryLocale = "en") {
  return [...countries].sort((a, b) =>
    getCountryName(a, lang).localeCompare(getCountryName(b, lang))
  )
}

export function getCountryContinent(country: CountryItem): CountryContinent {
  return countryContinentByCode[country.code] ?? "europe"
}

export type CountryListSection = {
  continent: CountryContinent
  title: string
  data: CountryItem[]
}

export function groupCountriesByContinent(
  countries: CountryItem[],
  lang: CountryLocale = "en"
): CountryListSection[] {
  const buckets = new Map<CountryContinent, CountryItem[]>()

  for (const country of countries) {
    const continent = getCountryContinent(country)
    const list = buckets.get(continent) ?? []
    list.push(country)
    buckets.set(continent, list)
  }

  return CONTINENT_ORDER.filter((continent) => buckets.has(continent)).map((continent) => {
    const data = buckets.get(continent) ?? []
    data.sort((a, b) => getCountryName(a, lang).localeCompare(getCountryName(b, lang)))
    return {
      continent,
      title: CONTINENT_LABELS[continent],
      data,
    }
  })
}

export function filterCountriesBySearch(
  countries: CountryItem[],
  searchValue: string,
  lang: CountryLocale = "en"
) {
  const query = searchValue.trim()
  if (!query) {
    return countries
  }

  const lowerQuery = query.toLowerCase()
  const normalizedQuery = removeDiacritics(lowerQuery)

  return countries.filter((country) => {
    const name = getCountryName(country, lang)
    const lowerName = name.toLowerCase()
    return (
      country.dial_code.includes(query) ||
      lowerName.includes(lowerQuery) ||
      removeDiacritics(lowerName).includes(normalizedQuery)
    )
  })
}

export function getPopularCountries(countries: CountryItem[], popularCountryCodes?: string[]) {
  if (!popularCountryCodes?.length) {
    return []
  }

  const popular = new Set(popularCountryCodes.map((code) => code.toUpperCase()))
  return countries.filter((country) => popular.has(country.code))
}

export const allCountryCodes = countryCodes
